// TimeseriesSummaryView — the "Trend" view: a plain value-over-time line for a
// single numeric feature, the summary-card analogue of the standalone numeric
// LineChartBody.
//
// Stateless by design: the card owns the fetch (this view declares the
// 'timeseries' source in SUMMARY_VIEW_SOURCES) and hands the raw, ms-dated
// series in via `timeseries`. This body slices it to the active window and
// draws it. The styling (area fill + #8884d8 line, -45° date ticks) mirrors
// LineChartBody so the standalone chart and this view read as the same chart.
// It deliberately omits LineChartBody's fullscreen-only luxuries (Y-brush,
// wheel/pinch zoom) — a compact summary card never uses them.

import React, { useMemo } from 'react'
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts'
import type { SummaryViewBodyProps } from '../viewRegistry'
import { numericExtent, numericTickFormatter, numericYAxisWidth } from '../../../charts/axisWidth'
import { isoDay } from '../../../charts/bodies/boolAggregation'
import { dayTicks, formatDateLabel, yearBoundaries, yearBoundaryLineProps } from '../../../charts/timeAxis'

const TimeseriesSummaryView: React.FC<SummaryViewBodyProps> = ({ unit, window, timeseries = [] }) => {
  // Slice to the active window ([start, end), half-open). 'All' has non-finite
  // bounds — show every point. Points arrive sorted, but sort defensively.
  const data = useMemo(() => {
    const { start, end } = window
    const bounded = Number.isFinite(start) && Number.isFinite(end)
    const pts = bounded
      ? timeseries.filter((p) => p.date >= start && p.date < end)
      : timeseries
    return [...pts].sort((a, b) => a.date - b.date)
  }, [timeseries, window])

  // Size the Y axis to its widest value label so "22.24" isn't clipped, without
  // a fat margin when labels are short.
  const yAxisWidth = useMemo(() => {
    const [lo, hi] = numericExtent(data.map((d) => d.value))
    return numericYAxisWidth(lo, hi, { fontSizePx: 15 })
  }, [data])

  // Drive ~5 ticks ourselves, matching LineChartBody — recharts' time-scale
  // auto-ticks otherwise label every point and the axis reads as a wall of
  // dates. Each sits on a day, so a few-day span doesn't repeat or misplace
  // one (`dayTicks`). `data` is sorted, so [0] is the earliest.
  const xTicks = useMemo<number[] | undefined>(
    () => (data.length === 0 ? undefined : dayTicks(data[0].date, data[data.length - 1].date)),
    [data],
  )

  // Year lines, so the year reads without spending tick labels on it.
  const yearLines = useMemo<number[]>(
    () => (data.length < 2 ? [] : yearBoundaries(data[0].date, data[data.length - 1].date)),
    [data],
  )

  if (data.length === 0) {
    return (
      <div data-testid="summary-timeseries-view" data-point-count={0}>
        <div className="flex h-64 items-center justify-center text-sm text-gray-500">
          <p>No values in this period.</p>
        </div>
      </div>
    )
  }

  return (
    <div
      data-testid="summary-timeseries-view"
      data-point-count={data.length}
      data-points={JSON.stringify(data.map((p) => [isoDay(new Date(p.date)), p.value]))}
    >
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 25 }}>
            <XAxis
              dataKey="date"
              type="number"
              domain={['dataMin', 'dataMax']}
              scale="time"
              ticks={xTicks}
              interval={0}
              tickFormatter={(ts: number) => formatDateLabel(ts, false)}
              angle={-45}
              textAnchor="end"
              height={50}
              tick={{ fontSize: 15 }}
              tickLine={false}
            />
            <YAxis tick={{ fontSize: 15 }} width={yAxisWidth} tickFormatter={numericTickFormatter} domain={['dataMin', 'dataMax']} tickLine={false} />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload || payload.length === 0) return null
                const value = payload[0].value as number
                const ts = payload[0].payload.date as number
                const display = unit ? `${value} ${unit}` : `${value}`
                return (
                  <div className="rounded border border-gray-200 bg-white px-2 py-1 text-sm shadow-md">
                    <div className="text-gray-600">{formatDateLabel(ts, true)}</div>
                    <div className="text-gray-900">{display}</div>
                  </div>
                )
              }}
            />
            {yearLines.map((ts) => <ReferenceLine key={ts} {...yearBoundaryLineProps(ts)} />)}
            <Area
              type="monotone"
              dataKey="value"
              stroke="#8884d8"
              strokeWidth={2}
              fill="#8884d8"
              fillOpacity={0.2}
              dot={false}
              activeDot={{ r: 8 }}
              name="Value"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default TimeseriesSummaryView
